import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { database, functions } from "../Components/firebase";
import {
  ref,
  onValue,
  query,
  orderByChild,
  equalTo,
  get,
} from "firebase/database";
import { detectStorageSystem } from "../Components/transactionLoader";
import { getTransactionDescription } from "../utils/transactionDescription";

/**
 * TransactionContext - Global State for Company Transactions
 *
 * Purpose: Maintain a single real-time listener per company across all components
 *
 * Benefits:
 * - One Firebase connection per company (shared by all report pages)
 * - Persistent listener (stays active during navigation)
 * - Instant page transitions (data already loaded)
 * - 75% bandwidth reduction (no duplicate downloads)
 * - Auto-sync across all open pages
 *
 * Usage:
 * - Wrap App.js with <TransactionProvider>
 * - Components use useAllYearsTransactions hook (updated to use this context)
 */

export const TransactionContext = createContext();

// Helper function to estimate bandwidth usage
const estimateDataSize = (data, label) => {
  try {
    const bytes = new TextEncoder().encode(JSON.stringify(data)).length;
    const mb = (bytes / (1024 * 1024)).toFixed(2);
    if (mb > 1.0) {
      console.log(`🚨 [High Bandwidth] ${label}: ${mb} MB downloaded!`);
    } else {
      //  const kb = (bytes / 1024).toFixed(2);
      // console.log(`📉 [Bandwidth] ${label}: ${kb} KB`);
    }
  } catch (err) {
    console.warn(`⚠️ [Bandwidth] Could not estimate size for ${label}:`, err);
  }
};

export const TransactionProvider = ({ children }) => {
  // State to store transactions for each company
  // Format: { "companyId": { transactions: [...], loading: false, error: null } }
  const [companyData, setCompanyData] = useState({});

  // Track active listeners to prevent duplicates and enable cleanup
  // Format: { "uid_companyId": unsubscribeFunction }
  const listenersRef = useRef({});

  // Track pending subscriptions to prevent race conditions during async setup
  // Format: { "uid_companyId": boolean }
  const pendingRef = useRef({});

  /**
   * Subscribe to real-time updates for a company
   * Creates persistent onValue() listener that stays active across component navigation
   * Supports both unified and legacy storage systems
   */
  const subscribeToCompany = useCallback(async (uid, companyId) => {
    const listenerKey = `${uid}_${companyId}`;

    // Don't create duplicate listeners or race overlapping requests
    if (listenersRef.current[listenerKey]) {
      // console.log(`✅ [TransactionContext] Already subscribed to ${companyId}`);
      return;
    }

    if (pendingRef.current[listenerKey]) {
      // console.log(`⏳ [TransactionContext] Subscription already in progress for ${companyId}`);
      return;
    }

    // Mark as pending immediately
    pendingRef.current[listenerKey] = true;

    console.log(
      `🔔 [TransactionContext] Subscribing to real-time updates for ${companyId}...`,
    );

    // Set loading state
    setCompanyData((prev) => ({
      ...prev,
      [companyId]: {
        transactions: prev[companyId]?.transactions || [],
        loading: true,
        error: null,
        lastUpdated: null,
      },
    }));

    try {
      // Detect storage system
      const storageSystem = await detectStorageSystem(uid, companyId);
      console.log(
        `📁 [TransactionContext] Storage system: ${storageSystem} for ${companyId}`,
      );

      const startTime = performance.now();

      // Skip creating listeners for Firestore companies (they use hooks instead)
      if (storageSystem === "firestoreUnified") {
        console.log(
          `🔥 [TransactionContext] Skipping listener for Firestore company ${companyId} - using hooks instead`,
        );

        // Remove pending flag since we're done (even though we didn't add a listener)
        delete pendingRef.current[listenerKey];

        setCompanyData((prev) => ({
          ...prev,
          [companyId]: {
            transactions: [],
            loading: false,
            error: null,
            lastUpdated: new Date().toISOString(),
            storageSystem: "firestoreUnified",
          },
        }));
        return; // Don't create any listener
      }

      if (storageSystem === "unified") {
        // UNIFIED SYSTEM: Single listener for all transactions
        const unifiedRef = ref(
          database,
          `/User/${uid}/UnifiedTransactions/${companyId}`,
        );
        let fallbackAttempted = false;

        const normalizeUnifiedTransactions = (data = {}) => {
          const transactions = Object.keys(data).map((key) => ({
            id: key,
            ...data[key],
            year: data[key].year || "All Years",
            companyKey: "unified",
            yearKey: "unified",
            key: data[key].key || key,
            JE: data[key].JE || false,
            bankAccount: data[key].bankAccount || "",
            assessed: data[key].assessed || false,
            accountNames: getTransactionDescription(data[key]),
            openingBalance: data[key].openingBalance || false,
          }));

          return transactions.filter((t) => t.date !== "RetainedIncomeForTB");
        };

        const unsubscribe = onValue(
          unifiedRef,
          (snapshot) => {
            const duration = performance.now() - startTime;

            if (snapshot.exists()) {
              const data = snapshot.val() || {};

              // Log bandwidth usage for unified transaction download
              estimateDataSize(
                data,
                `Raw Transactions Download (Unified: ${companyId})`,
              );

              const filtered = normalizeUnifiedTransactions(data);

              // console.log(
              //   `📊 [TransactionContext] Unified: Received ${
              //     filtered.length
              //   } transactions for ${companyId} in ${duration.toFixed(0)}ms`
              // );

              setCompanyData((prev) => ({
                ...prev,
                [companyId]: {
                  transactions: filtered,
                  loading: false,
                  error: null,
                  lastUpdated: new Date().toISOString(),
                  storageSystem: "unified",
                },
              }));
            } else {
              console.log(
                `⚠️ [TransactionContext] No unified data found for ${companyId}`,
              );

              setCompanyData((prev) => ({
                ...prev,
                [companyId]: {
                  transactions: [],
                  loading: false,
                  error: null,
                  lastUpdated: new Date().toISOString(),
                  storageSystem: "unified",
                },
              }));
            }
          },
          async (error) => {
            const duration = performance.now() - startTime;
            console.error(
              `❌ [TransactionContext] Unified error for ${companyId} after ${duration.toFixed(
                0,
              )}ms:`,
              error,
            );

            const isPermissionDenied =
              (error?.code || "").toLowerCase().includes("permission") ||
              (error?.message || "").toLowerCase().includes("permission_denied");

            if (isPermissionDenied && !fallbackAttempted) {
              fallbackAttempted = true;
              console.warn(
                `⚠️ [TransactionContext] Permission denied for direct unified read on ${companyId}. Trying Cloud Function fallback...`,
              );

              try {
                const loadAllUnifiedData = functions.httpsCallable(
                  "LoadAllUnifiedTransactions",
                );
                const result = await loadAllUnifiedData({
                  clientId: companyId,
                  ownerUid: uid,
                });

                const txArray = Array.isArray(result?.data?.transactions)
                  ? result.data.transactions
                  : [];
                const normalizedFallback = txArray
                  .map((tx, index) => ({
                    id: tx?.id || tx?.key || `cf_${index}`,
                    year: tx?.year || "All Years",
                    companyKey: "unified",
                    yearKey: "unified",
                    key: tx?.key || tx?.id || `cf_${index}`,
                    JE: tx?.JE || false,
                    bankAccount: tx?.bankAccount || "",
                    assessed: tx?.assessed || false,
                    openingBalance: tx?.openingBalance || false,
                    ...tx,
                    accountNames: getTransactionDescription(tx),
                  }))
                  .filter((t) => t.date !== "RetainedIncomeForTB");

                setCompanyData((prev) => ({
                  ...prev,
                  [companyId]: {
                    transactions: normalizedFallback,
                    loading: false,
                    error: null,
                    lastUpdated: new Date().toISOString(),
                    storageSystem: "unified",
                  },
                }));
                return;
              } catch (fallbackError) {
                console.error(
                  `❌ [TransactionContext] Cloud Function fallback failed for ${companyId}:`,
                  fallbackError,
                );
              }
            }

            setCompanyData((prev) => ({
              ...prev,
              [companyId]: {
                transactions: [],
                loading: false,
                error: error.message || "Failed to load transactions",
                lastUpdated: new Date().toISOString(),
                storageSystem: "unified",
              },
            }));
          },
        );

        // Store the unsubscribe function
        listenersRef.current[listenerKey] = unsubscribe;
        // Remove pending flag now that listener is established
        delete pendingRef.current[listenerKey];

        console.log(
          `✅ [TransactionContext] Unified listener created for ${companyId}`,
        );
      } else {
        // LEGACY SYSTEM: Listener on companies filtered by sameCompanyKey

        // Resolve sameCompanyKey first (in case companyId is a child node)
        let targetSameCompanyKey = companyId;
        try {
          const companyRef = ref(
            database,
            `/BusinessDetails/${uid}/ClientCompanyAdded/${companyId}`,
          );
          const snapshot = await get(companyRef);
          if (snapshot.exists()) {
            targetSameCompanyKey = snapshot.val().sameCompanyKey || companyId;
          }
        } catch (err) {
          console.warn("Error resolving sameCompanyKey:", err);
        }

        const companiesRef = ref(database, `/User/${uid}/ClientCompanyAdded`);
        const legacyQuery = query(
          companiesRef,
          orderByChild("sameCompanyKey"),
          equalTo(targetSameCompanyKey),
        );

        const unsubscribe = onValue(
          legacyQuery,
          (snapshot) => {
            const duration = performance.now() - startTime;

            if (snapshot.exists()) {
              const allCompaniesData = snapshot.val() || {};

              // Log bandwidth usage for legacy transaction download
              estimateDataSize(
                allCompaniesData,
                `Raw Transactions Download (Legacy: ${companyId})`,
              );
              let allTransactions = [];

              // Iterate only matching company nodes (already filtered by sameCompanyKey)
              Object.keys(allCompaniesData).forEach((companyKey) => {
                const companyData = allCompaniesData[companyKey];

                // Get all year keys
                const yearKeys = Object.keys(companyData);
                //       .filter(
                //   (key) =>
                //     key !== "sameCompanyKey" &&
                //     key !== "storageSystem" &&
                //     key !== "companyNameRef"
                // );

                // Extract transactions from each year
                yearKeys.forEach((yearKey) => {
                  const yearData = companyData[yearKey];
                  if (yearData && yearData.Accounts) {
                    Object.keys(yearData.Accounts).forEach((accountKey) => {
                      const transaction = yearData.Accounts[accountKey];
                      allTransactions.push({
                        id: accountKey,
                        ...transaction,
                        year:
                          yearData.yearEndDate ||
                          yearData.AmendedYearEnd ||
                          yearKey,
                        companyKey,
                        yearKey,
                      });
                    });
                  }
                });
              });

              // Filter out retained income only (keep journals for journal page)
              const filtered = allTransactions.filter(
                (t) => t.date !== "RetainedIncomeForTB",
              );

              console.log(
                `📊 [TransactionContext] Legacy: Received ${
                  allTransactions?.length
                } transactions for ${companyId} in ${duration.toFixed(0)}ms`,
              );

              setCompanyData((prev) => ({
                ...prev,
                [companyId]: {
                  transactions: filtered,
                  loading: false,
                  error: null,
                  lastUpdated: new Date().toISOString(),
                  storageSystem: "legacy",
                },
              }));
            } else {
              console.log(
                `⚠️ [TransactionContext] No legacy data found for ${companyId}`,
              );

              setCompanyData((prev) => ({
                ...prev,
                [companyId]: {
                  transactions: [],
                  loading: false,
                  error: null,
                  lastUpdated: new Date().toISOString(),
                  storageSystem: "legacy",
                },
              }));
            }
          },
          (error) => {
            const duration = performance.now() - startTime;
            console.error(
              `❌ [TransactionContext] Legacy error for ${companyId} after ${duration.toFixed(
                0,
              )}ms:`,
              error,
            );

            setCompanyData((prev) => ({
              ...prev,
              [companyId]: {
                transactions: [],
                loading: false,
                error: error.message || "Failed to load transactions",
                lastUpdated: new Date().toISOString(),
                storageSystem: "legacy",
              },
            }));
          },
        );

        // Store the unsubscribe function
        listenersRef.current[listenerKey] = unsubscribe;
        // Remove pending flag now that listener is established
        delete pendingRef.current[listenerKey];

        console.log(
          `✅ [TransactionContext] Legacy listener created for ${companyId}`,
        );
      }
    } catch (error) {
      // Remove pending flag on error so retry is possible
      delete pendingRef.current[listenerKey];

      console.error(
        `❌ [TransactionContext] Error setting up listener for ${companyId}:`,
        error,
      );

      setCompanyData((prev) => ({
        ...prev,
        [companyId]: {
          transactions: [],
          loading: false,
          error: error.message || "Failed to initialize listener",
          lastUpdated: new Date().toISOString(),
        },
      }));
    }
  }, []);

  /**
   * Unsubscribe from a company's updates
   * Called when switching companies or on app unmount
   */
  const unsubscribeFromCompany = useCallback((uid, companyId) => {
    const listenerKey = `${uid}_${companyId}`;

    if (listenersRef.current[listenerKey]) {
      console.log(`🔕 [TransactionContext] Unsubscribing from ${companyId}`);

      // Call the unsubscribe function to detach listener
      listenersRef.current[listenerKey]();

      // Remove from tracking
      delete listenersRef.current[listenerKey];
      delete pendingRef.current[listenerKey]; // Just in case

      console.log(`✅ [TransactionContext] Listener removed for ${companyId}`);
    }
  }, []);

  /**
   * Get transactions for a specific company
   * Returns empty array if not loaded yet
   */
  const getTransactions = useCallback(
    (companyId) => {
      return companyData[companyId]?.transactions || [];
    },
    [companyData],
  );

  /**
   * Check if a company's data is currently loading
   */
  const isLoading = useCallback(
    (companyId) => {
      return companyData[companyId]?.loading || false;
    },
    [companyData],
  );

  /**
   * Get error message for a company (if any)
   */
  const getError = useCallback(
    (companyId) => {
      return companyData[companyId]?.error || null;
    },
    [companyData],
  );

  /**
   * Get metadata about loaded data
   */
  const getMetadata = useCallback(
    (companyId) => {
      return {
        loaded: !!companyData[companyId],
        count: companyData[companyId]?.transactions?.length || 0,
        lastUpdated: companyData[companyId]?.lastUpdated || null,
        loading: companyData[companyId]?.loading || false,
        error: companyData[companyId]?.error || null,
      };
    },
    [companyData],
  );

  /**
   * Cleanup all listeners when provider unmounts
   */
  useEffect(() => {
    return () => {
      console.log("🧹 [TransactionContext] Cleaning up all listeners...");
      Object.keys(listenersRef.current).forEach((key) => {
        if (listenersRef.current[key]) {
          listenersRef.current[key]();
        }
      });
      listenersRef.current = {};
      pendingRef.current = {};
    };
  }, []);

  // Debug: Log active listeners count
  // useEffect(() => {
  //   const activeCount = Object.keys(listenersRef.current).length;
  //   const loadedCount = Object.keys(companyData).length;
  //   // console.log(
  //   //   `📊 [TransactionContext] Active listeners: ${activeCount}, Loaded companies: ${loadedCount}`
  //   // );
  // }, [companyData]);

  const contextValue = {
    // State
    companyData,

    // Actions
    subscribeToCompany,
    unsubscribeFromCompany,

    // Getters
    getTransactions,
    isLoading,
    getError,
    getMetadata,
  };

  return (
    <TransactionContext.Provider value={contextValue}>
      {children}
    </TransactionContext.Provider>
  );
};

// Export hook for easier usage
export const useTransactionContext = () => {
  const context = React.useContext(TransactionContext);
  if (!context) {
    throw new Error(
      "useTransactionContext must be used within a TransactionProvider",
    );
  }
  return context;
};
